/**
 * 菜单可见性过滤（2026-08-24 从 App.tsx 抽出，为的是能被测试覆盖）。
 *
 * 为什么值得单独测：944 个已登记账号里 **765 个没有任何模块权限**，他们能看见的
 * 唯一入口就是「知识投稿」。2026-08-24 把投稿从顶级挪进「知识库」组时，如果只按
 * `has(childKey)` 过滤，这 765 人会连投稿一起丢掉——等于把"开放投稿"悄悄关掉，
 * 而且没人会立刻发现（他们本来就看不到别的东西）。这类回归必须有测试守着。
 */

export type MenuNode = {
  key?: string;
  type?: string;
  alwaysVisible?: boolean;
  permKey?: string;
  permKeys?: string[];
  children?: MenuNode[];
};

/** 过滤菜单：isAll=拥有全部模块；modules=已授权模块键列表。 */
export function filterMenu(items: MenuNode[], isAll: boolean, modules: string[]): MenuNode[] {
  const has = (k?: string) => !!k && !isAll && modules.includes(k);
  return items
    .map((it) => {
      if (it.type === "divider") return it;
      if (it.alwaysVisible) return it;                       // 顶级公共入口
      if (it.children) {
        if (isAll || has(it.key)) return it;                 // 整组授权 → 全部子项
        // 子项也支持 alwaysVisible（知识投稿在「知识库」组内但对全员开放）
        const kids = it.children.filter((c) => c.alwaysVisible || (c.permKeys ? c.permKeys.some(has) : has(c.permKey || c.key)));
        return kids.length ? { ...it, children: kids } : null;
      }
      const pk = it.permKey || it.key;
      return isAll || has(pk) || has(String(pk).replace(/^\//, "")) ? it : null;
    })
    .filter(Boolean) as MenuNode[];
}

/** 这个节点是否"只含公共入口"（用于判断账号有没有任何业务权限）。 */
export function isPublicOnlyNode(i: MenuNode): boolean {
  return (
    i.type === "divider" ||
    !!i.alwaysVisible ||
    (!!i.children && i.children.every((c) => !!c.alwaysVisible))
  );
}

/**
 * 过滤后是否"只剩公共入口" → 该账号没有任何业务模块授权，应落地到投稿页。
 * loaded 必须为真才判断，否则首屏会把有权限的用户也重定向走。
 */
export function isOnlyPublicAccess(
  filtered: MenuNode[], isAll: boolean, loaded: boolean,
): boolean {
  return loaded && !isAll && filtered.every(isPublicOnlyNode);
}
